/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { MockedProvider, MockedResponse } from "@apollo/client/testing";
import OktaAuth from "@okta/okta-auth-js";
import { PL_DEFAULT_THEME, ThemeProvider } from "@powerledger/ui-component-lib";
import { render as rtlRender } from "@testing-library/react";
import { renderHook as rtlRenderHook } from "@testing-library/react-hooks/dom/pure";
import * as ConfigCat from "configcat-react";
import { Formik } from "formik";
import { createMemoryHistory } from "history";
import { JSXElementConstructor, ReactElement } from "react";
import { Router } from "react-router-dom";

type ReactChildren = ReactElement<any, string | JSXElementConstructor<any>>;

export const renderHook = <TProp extends { children: ReactChildren }, TResult>(
  hook: (props: TProp) => TResult,
  mocks?: Array<MockedResponse>,
  formikValues: Record<string, any> = {},
  history = appHistory,
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <Router navigator={history} location={history.location}>
      <Formik initialValues={formikValues} onSubmit={() => {}}>
        <MockedProvider mocks={mocks}>{children}</MockedProvider>
      </Formik>
    </Router>
  );

  return rtlRenderHook<TProp, TResult>(hook, { wrapper });
};

export const appHistory = createMemoryHistory();

export const renderWithProviders = (
  ui: ReactChildren,
  { graphqlMocks = [], ...renderOptions }: { graphqlMocks?: Array<MockedResponse> } = {},
  history = appHistory,
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <ThemeProvider theme={PL_DEFAULT_THEME}>
      <Router navigator={history} location={history.location}>
        <MockedProvider addTypename={false} mocks={graphqlMocks}>
          {children}
        </MockedProvider>
      </Router>
    </ThemeProvider>
  );

  return rtlRender(ui, { wrapper, ...renderOptions });
};

export const render = (ui: ReactChildren, graphqlMocks?: Array<MockedResponse>) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <MockedProvider addTypename={false} mocks={graphqlMocks}>
      {children}
    </MockedProvider>
  );

  return rtlRender(ui, { wrapper });
};

export const mockFeatureToggles = ({
  toggles = [],
  toggled = true,
  defaultToggle = true,
}: {
  toggles?: string[];
  toggled?: boolean;
  defaultToggle?: boolean;
}) => {
  vi.spyOn(ConfigCat, "useFeatureFlag").mockImplementation((key: string, defaultValue: any, user?: any) => ({
    value: toggles.includes(key) ? toggled : defaultToggle,
    loading: false,
  }));
};

export const oktaAuthNotLoggedIn = new OktaAuth({
  issuer: 'https://{yourOktaDomain}.com/oauth2/{authServerId}"',
  clientId: "OKTA_CLIENT_ID",
  redirectUri: window.location.origin + "/login/callback",
});

export const oktaAuthLoggedIn = new OktaAuth({
  issuer: 'https://{yourOktaDomain}.com/oauth2/{authServerId}"',
  clientId: "OKTA_CLIENT_ID",
  redirectUri: window.location.origin + "/login/callback",
  transformAuthState: async (oktaAuth, authState) => {
    authState.isAuthenticated = true;
    oktaAuth.storageManager.getTokenStorage().setItem("accessToken", { accessToken: "eyJraWQ" });
    return authState;
  },
});
